class PersonalisationDrawer extends HTMLElement {
  constructor() {
    super();

    this.personalisationConfirm = document.querySelector('personalisation-confirm');
    this.confirmButton = this.querySelector('#PersonalisationConfirm');
    this.setEventListeners();
    this.disableConfirmButton();
  }

  setEventListeners() {
    const personalisationButton = document.querySelector('#PersonalisationDrawerOpener');
    const initialsInput = this.querySelector('#PersonalisationInitials');
    const termsInput = this.querySelector('#AcceptsPersonalisationTerms');
    const fontRadioGroup = this.querySelector('.personalisation-drawer__fonts');
    const colorRadioGroup = this.querySelector('.personalisation-drawer__thread-colors');
    const overlay = this.querySelector('#PersonalisationDrawer-Overlay');

    // Add escape key listener
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.closeDrawer());
    
    // Add overlay click listener
    if (overlay) {
      overlay.addEventListener('click', this.closeDrawer.bind(this));
    }

    // Add personalisation button listener
    if (personalisationButton) {
      personalisationButton.addEventListener('click', () => {
        this.openDrawer(personalisationButton);
        this.disableConfirmButton();
      });
    }

    // Add initials input listener
    if (initialsInput) {
      initialsInput.addEventListener('input', (event) => {
        this.checkInitialsLength(event.target);
      });
    }

    // Add confirm button listener
    if (this.confirmButton) {
      this.confirmButton.addEventListener('click', () => {
        this.addPersonalisationToProduct();
        this.updateAddToCartButton();
      });
    }

    // Add terms checkbox listener
    if (termsInput) {
      termsInput.addEventListener('click', (event) => {
        if (event.target.checked) {
          this.confirmButton.removeAttribute('disabled');
          this.querySelector('#AcceptsPersonalisationTerms').classList.remove('error');
        } else {
          this.confirmButton.setAttribute('disabled', 'disabled');
        }
      });
    }

    // Add font radio group listener
    if (fontRadioGroup) {
      fontRadioGroup.addEventListener('change', () => {
        const radioValue = this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__font-radio'));

        if (radioValue) {
          const fontErrorWrapper = this.querySelector('.personalisation-drawer__note--font');

          if (fontErrorWrapper) {
            fontErrorWrapper.classList.remove('error');
            fontErrorWrapper.innerHTML = radioValue;
          }
        }
      });
    }

    // Add color radio group listener
    if (colorRadioGroup) {
      colorRadioGroup.addEventListener('change', () => {
        const radioValue = this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__color-radio'));

        if (radioValue) {
          const colorErrorWrapper = this.querySelector('.personalisation-drawer__note--color');

          if (colorErrorWrapper) {
            colorErrorWrapper.classList.remove('error');
            colorErrorWrapper.innerHTML = radioValue;
          }
        }
      });
    }
  }

  openDrawer() {
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('PersonalisationDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden--drawer-open');
    document.documentElement.classList.add('overflow-hidden--drawer-open');
  }

  closeDrawer() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden--drawer-open');
    document.documentElement.classList.remove('overflow-hidden--drawer-open');
  }

  checkInitialsLength(element) {

    // Get dynamic character limit from data attribute or fallback to 3
    const initialsMax = parseInt(element.getAttribute('data-max-length')) || 3;
    const format = /^[A-Za-z]+$/;
    const hasOnlyLetters = format.test(element.value);

    if (!hasOnlyLetters) {
      element.value = element.value.replace(/[^\w\s]/gi, '').replace(/[0-9_]/gi, '');
    }

    const initialsLength = element.value.length;

    if (initialsLength > 0) {
      const initialsNote = this.querySelector('.personalisation-drawer__note--initials');
      if (initialsNote) {
        initialsNote.innerHTML = '';
      }
    }

    if (initialsLength > initialsMax) {
      element.value = element.value.substring(0, initialsMax);
    } else {
      element.value = element.value.toUpperCase();
      const initialsCount = this.querySelector('.personalisation-drawer__initials-count');
      if (initialsCount) {
        initialsCount.innerHTML = initialsLength + '/' + initialsMax;
      }
    }
  }

  addPersonalisationToProduct() {
    const personalisedData = {
      initials: this.querySelector('#PersonalisationInitials').value,
      font: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__font-radio')),
      color: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__color-radio')),
      termsChecked: this.isTermsChecked(),
      variantId: document.querySelector('#PersonalisationDrawerOpener').dataset.variantId,
    };

    this.errorHandling(personalisedData);

    const isSomeDataMissing = Object.keys(personalisedData).some(
      (key) => personalisedData[key] === '' || personalisedData[key] === false
    );

    if (!isSomeDataMissing) {
      this.closeDrawer();
      this.personalisationConfirm.handlePersonalisedDataUpdate(personalisedData);
      this.personalisationConfirm.enablePersonalisationPropertyInputs();
    }
  }

  removePersonalization() {
    // Empty product properties
    const productInitials = document.querySelector('#ProductPropertiesInitials');
    const productFont = document.querySelector('#ProductPropertiesFont');
    const productColor = document.querySelector('#ProductPropertiesColor');
    
    if (productInitials) productInitials.value = '';
    if (productFont) productFont.value = '';
    if (productColor) productColor.value = '';

    // Clear personalisation data in confirm box
    const confirmInitials = document.querySelector('#ProductConfirmInitials');
    const confirmFont = document.querySelector('#ProductConfirmFont');
    const confirmColor = document.querySelector('#ProductConfirmColor');
    
    if (confirmInitials) confirmInitials.innerHTML = '';
    if (confirmFont) confirmFont.innerHTML = '';
    if (confirmColor) confirmColor.innerHTML = '';

    // Set Personalisation Drawer inputs to default
    const drawerInitials = this.querySelector('#PersonalisationInitials');
    const drawerTerms = this.querySelector('#AcceptsPersonalisationTerms');
    
    if (drawerInitials) drawerInitials.value = '';
    if (drawerTerms) drawerTerms.checked = false;
    
    // Clear selected radio buttons safely
    const selectedFont = this.querySelector('input[name="personalisation-font"]:checked');
    if (selectedFont) {
      selectedFont.checked = false;
    }
    
    const selectedColor = this.querySelector('input[name="personalisation-color"]:checked');
    if (selectedColor) {
      selectedColor.checked = false;
    }

    // Remove selected data info from font and color heading
    const fontNote = this.querySelector('.personalisation-drawer__note--font');
    const colorNote = this.querySelector('.personalisation-drawer__note--color');
    const initialsCount = this.querySelector('.personalisation-drawer__initials-count');
    
    if (fontNote) fontNote.innerHTML = '';
    if (colorNote) colorNote.innerHTML = '';
    
    // Reset initials count with dynamic character limit
    if (initialsCount) {
      const initialsInput = this.querySelector('#PersonalisationInitials');
      const maxLength = initialsInput ? parseInt(initialsInput.getAttribute('data-max-length')) || 3 : 3;
      initialsCount.innerHTML = '0/' + maxLength;
    }

    if (this.personalisationConfirm) {
      this.personalisationConfirm.disablePersonalisationPropertyInputs();
    }
  }

  errorHandling(personalisedData) {
    if (!personalisedData.initials) {
      const initialsError = this.querySelector('.personalisation-drawer__note--initials');
      if (initialsError) {
        initialsError.innerHTML = window.personalisationStrings.initialsError;
      }
    }

    if (!personalisedData.font) {
      const fontErrorWrapper = this.querySelector('.personalisation-drawer__note--font');

      if (fontErrorWrapper) {
        fontErrorWrapper.classList.add('error');
        fontErrorWrapper.innerHTML = window.personalisationStrings.fontError;
      }
    }

    if (!personalisedData.color) {
      const colorErrorWrapper = this.querySelector('.personalisation-drawer__note--color');

      if (colorErrorWrapper) {
        colorErrorWrapper.classList.add('error');
        colorErrorWrapper.innerHTML = window.personalisationStrings.colorError;
      }
    }

    if (!personalisedData.termsChecked) {
      const termsElement = this.querySelector('#AcceptsPersonalisationTerms');
      if (termsElement) {
        termsElement.classList.add('error');
      }
    }
  }

  updateAddToCartButton() {
    const section = this.dataset.originalSection || this.dataset.section;
    const variantSelects = document.querySelector('variant-selects');
    const addToCartButton = document.querySelector(`#ProductSubmitButton-${section}`);
    const isAddToCartDisabled = addToCartButton.hasAttribute('disabled');

    if (variantSelects) {
      variantSelects.toggleAddButton(isAddToCartDisabled, '');
    }
  }

  isTermsChecked() {
    const termsInput = this.querySelector('#AcceptsPersonalisationTerms');
    return termsInput.checked;
  }

  disableConfirmButton() {
    if (this.isTermsChecked()) {
      this.confirmButton.removeAttribute('disabled');
    } else {
      this.confirmButton.setAttribute('disabled', 'disabled');
    }
  }

  getRadioGroupValue(radioGroup) {
    let radioValue = '';

    radioGroup.forEach((radio) => {
      if (radio.checked) {
        radioValue = radio.value;
      }
    });

    return radioValue;
  }
}

customElements.define('personalisation-drawer', PersonalisationDrawer);

class PersonalisationConfirm extends HTMLElement {
  constructor() {
    super();

    this.personalisationDrawer = document.querySelector('personalisation-drawer');
    this.personalisationConfirmBox = this.querySelector('#PersonalisationConfirmBox');
    this.personalisationButton = document.querySelector('#PersonalisationDrawerOpener');
    this.setEventListeners();
  }

  setEventListeners() {
    const confirmEditButton = this.querySelector('#PersonalisationConfirmEdit');
    const confirmRemoveButton = this.querySelector('#PersonalisationConfirmRemove');
    
    if (confirmEditButton) {
      confirmEditButton.addEventListener('click', () => {
        if (this.personalisationDrawer) {
          this.personalisationDrawer.openDrawer(this.personalisationButton);
        }
      });
    }

    if (confirmRemoveButton) {
      confirmRemoveButton.addEventListener('click', () => {
        if (this.personalisationButton) {
          this.personalisationButton.style.display = 'block';
        }
        if (this.personalisationConfirmBox) {
          this.personalisationConfirmBox.style.display = 'none';
        }
        if (this.personalisationDrawer) {
          this.personalisationDrawer.removePersonalization();
          this.personalisationDrawer.updateAddToCartButton();
        }
      });
    }
  }

  updateProductData(personalisedData) {
    const initialsInput = this.querySelector('#ProductPropertiesInitials');
    const fontInput = this.querySelector('#ProductPropertiesFont');
    const colorInput = this.querySelector('#ProductPropertiesColor');
    
    if (initialsInput) initialsInput.value = personalisedData.initials;
    if (fontInput) fontInput.value = personalisedData.font;
    if (colorInput) colorInput.value = personalisedData.color;
  }

  updateConfirmBox(personalisedData) {
    const confirmInitials = this.querySelector('#ProductConfirmInitials');
    const confirmFont = this.querySelector('#ProductConfirmFont');
    const confirmColor = this.querySelector('#ProductConfirmColor');
    
    if (confirmInitials) confirmInitials.innerHTML = personalisedData.initials;
    if (confirmFont) confirmFont.innerHTML = personalisedData.font;
    if (confirmColor) confirmColor.innerHTML = personalisedData.color;
    
    if (this.personalisationButton) {
      this.personalisationButton.style.display = 'none';
    }
    if (this.personalisationConfirmBox) {
      this.personalisationConfirmBox.style.display = 'block';
    }
  }

  handlePersonalisedDataUpdate(personalisedData) {
    this.updateProductData(personalisedData);
    this.updateConfirmBox(personalisedData);
  }

  enablePersonalisationPropertyInputs() {
    const properties = this.querySelectorAll('.personalisation-property');

    properties.forEach((property) => {
      property.removeAttribute('disabled');
    });
  }

  disablePersonalisationPropertyInputs() {
    const properties = this.querySelectorAll('.personalisation-property');

    properties.forEach((property) => {
      property.setAttribute('disabled', 'disabled');
    });
  }
}

customElements.define('personalisation-confirm', PersonalisationConfirm);
